import {Component, NgZone, OnInit} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {
    IncidentsJSON,
    IncidentsService,
    ScheduledMaintenanceFilter, ScheduledMaintenanceJSON
} from '@webmate-web-core/shared/services/incidents.service';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {PosthogService} from '../webmate/shared/services/posthog.service';
import {AuthService} from '@webmate-web-core/shared/services/auth.service';
import {ToastCloseEvent} from 'primeng/toast';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
    // We need to inject this service here, otherwise it won't be initialized
    constructor(private router: Router,
                private notificationService: WmNotificationService, private incidentsService: IncidentsService, private webmateOptions: WebmateOptions,
                private postHog: PosthogService,
                private OAuth2AuthService: AuthService,
                private ngZone: NgZone) {}

    private lastIncidentUpdate?: number = undefined;


    ngOnInit() {
        this.checkIncidents();
        this.checkScheduledMaintenances();

        setInterval(() => this.checkIncidents(), 60_000); // every minute
        setInterval(() => this.checkScheduledMaintenances(), 60_000); // every minute

        // Init Posthog
        this.postHog.init();

        if (this.OAuth2AuthService.isInImpersonationMode()) {
            this.postHog.disableTracking();
        } else {
            this.postHog.enableTracking();
        }

        // Backwards compatibility for stored HashLocationStrategy urls
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart && event.url.startsWith('/#')))
            .subscribe((event) => {
                this.router.navigateByUrl((event as NavigationStart).url.replace('/#', ''))
            });

        this.loadMarkerIo(this.webmateOptions.markerIOProject);
    }

    private checkIncidents() {
        let onlyUnresolved: boolean = false;
        if (!this.lastIncidentUpdate) {
            onlyUnresolved = true;
            this.lastIncidentUpdate = 0;
        }
        this.incidentsService.fetchIncidents(onlyUnresolved).subscribe(json => this.handleIncidents(json));
    }

    private handleIncidents(json: IncidentsJSON) {
        const update = Date.parse(json.page.updated_at);
        if (update > this.lastIncidentUpdate!) {
            json.incidents
                .filter(incident => Date.parse(incident.updated_at) > this.lastIncidentUpdate!)
                .forEach(incident => incident.incident_updates
                    .filter(update => Date.parse(update.updated_at) > this.lastIncidentUpdate!)
                    .forEach(update => this.notificationService.toastIncidentNotification(update.body, incident.name, update.status))
                );
            this.lastIncidentUpdate = update;
        }
    }

    private checkScheduledMaintenances() {
        this.incidentsService.fetchScheduledMaintenances(ScheduledMaintenanceFilter.ALL).subscribe(json => this.handleScheduledMaintenances(json));
    }

    private handleScheduledMaintenances(json: ScheduledMaintenanceJSON) {
        // deduplication (already notified / dismissed) happens inside the notification service,
        // so every maintenance update can simply be reported on every poll
        json.scheduled_maintenances.forEach(maintenance => maintenance.incident_updates
            .forEach(update => this.notificationService.toastScheduledMaintenanceNotification(maintenance.id, update.body, maintenance.name, update.status, maintenance.scheduled_for, maintenance.scheduled_until, update.updated_at))
        );
    }

    onScheduledMaintenanceToastClose(event: ToastCloseEvent) {
        this.notificationService.onScheduledMaintenanceToastClose(event);
    }

    private loadMarkerIo(projectId?: string): void {
        if (!projectId) {
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://edge.marker.io/latest/shim.js';
        script.async = true;

        (window as any).markerConfig = { project: projectId };

        document.head.appendChild(script);
    }
}
